import React, { useState } from 'react'

import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Add, Download, Upload } from '@factorialco/f0-react/icons/app'
import {
  ClientsListPageDocument,
  ClientsTableDocument,
  ClientsTableQuery,
  FinanceValueObjectsContactTypeEnum,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { convertDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import ContactQuickAddFormModal from 'modules/finance/components/Contacts/ContactQuickAddFormModal'
import { ContactsImportModal } from 'modules/finance/components/Contacts/ContactsImportModal'
import CollectionsBootstrap from 'modules/finance/lib/ContactsCollectionsBootstrap'
import { contactFullName } from 'modules/finance/lib/contactsUtils'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { ExportSidepanel } from 'modules/universal_exports/components/ExportSidepanel'

type Client = ClientsTableQuery['finance']['contactsConnection']['nodes'][number]

const Container: React.FC = () => {
  const { data, loading, error } = useQuery(ClientsListPageDocument)
  const [showModal, setShowModal] = useState<boolean>(false)
  const [showImportModal, setShowImportModal] = useState<boolean>(false)
  const [showExportModal, setShowExportModal] = useState<boolean>(false)
  const authorized = data?.finance._policies.sidebarSeeFinanceClientsSubLevelItem.can ?? false

  const { navigation } = useNavigation()

  const openModal = () => {
    track(trackingEvents.accounting.openNewContactModal)
    setShowModal(true)
  }
  const handleExport = () => {
    setShowExportModal(true)
  }

  const handleImport = () => {
    track(trackingEvents.accounting.contacts.clickImportContacts)
    setShowImportModal(true)
  }

  const { company } = useCurrentSession()

  useEffect(() => {
    track(trackingEvents.accounting.viewClientsListPage)
  }, [])

  const source = useGraphqlDataCollectionSource({
    document: ClientsTableDocument,
    mapData: (tableData) => tableData.finance.contactsConnection.nodes,
    search: {
      enabled: true,
    },
    primaryActions: () => ({
      label: i18n.t('finance.contacts.contacts_list.new_client'),
      icon: Add,
      onClick: () => openModal(),
    }),
    secondaryActions: () => [
      {
        label: i18n.t('finance.contacts.contacts_list.export_contacts'),
        icon: Download,
        onClick: () => handleExport(),
      },
      {
        label: i18n.t('finance.contacts.contacts_list.import_contacts'),
        icon: Upload,
        onClick: () => handleImport(),
      },
    ],
    mapVariables: ({ search }) => ({
      companyId: company.id,
      search: search,
      // eslint-disable-next-line no-type-assertion/no-type-assertion
      contactType: 'client' as FinanceValueObjectsContactTypeEnum,
    }),
    mapPagination: (tableData) => ({
      total: tableData.finance.contactsConnection.totalCount ?? 0,
    }),
    itemUrl: (item: Client) =>
      navigation.finance.sales.clients.contactId.fullPath({ contactId: item.id }),
  })

  return (
    <ResponsivePage.Body authorized={authorized} error={error} loading={loading} variant="full">
      {showModal && (
        <ContactQuickAddFormModal
          onClose={() => {
            setShowModal(false)
          }}
          contactType={'client'}
        />
      )}
      {showImportModal && (
        <ContactsImportModal contactType={'client'} onClose={() => setShowImportModal(false)} />
      )}
      {showExportModal && (
        <ExportSidepanel resource="finance.contact" onClose={() => setShowExportModal(false)} />
      )}
      <OneDataCollection
        source={source}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: [
                {
                  label: i18n.t('finance.contacts.contacts_list.table.contact'),
                  render: (item: Client) => contactFullName(item),
                },
                {
                  label: i18n.t('finance.contacts.contacts_list.table.tax_id'),
                  render: (item: Client) => item.taxId || undefined,
                },
                {
                  label: i18n.t('finance.contacts.contacts_list.table.created_at'),
                  render: (item: Client) =>
                    item.createdAt
                      ? convertDate(item.createdAt, 'backend_datetime', 'ui_dmy_long')
                      : undefined,
                },
              ],
            },
          },
        ]}
      />
    </ResponsivePage.Body>
  )
}

const ClientsListPage: React.FC = () => {
  return (
    <CollectionsBootstrap>
      <Container />
    </CollectionsBootstrap>
  )
}

export default ClientsListPage
